import React from 'react';
import classNames from 'classnames';
import { Card, Col, Form, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CashFlowChart from 'components/app/landdev/summary-report-comparison/CashFlowChart';
import { useSelector } from 'react-redux';
import Projects from '../dashboard/projects';
import ExportToXLSX from 'components/common/export-file/ExportToXLSX';
import ExpenseBifurcation from './ExpenseBifurcation';

const FormCheck = ({ title, id, inputClass, checkBoxClass, handleLegend }) => {
  return (
    <Form.Check
      className={classNames(checkBoxClass, 'd-flex align-items-center mb-0')}
    >
      <Form.Check.Input
        onChange={event => handleLegend(event, title)}
        type="checkbox"
        id={id}
        defaultChecked
        className={classNames(
          inputClass,
          'dot mt-0 shadow-none remove-checked-icon rounded-circle cursor-pointer'
        )}
      />
      <Form.Check.Label
        htmlFor={id}
        className="form-check-label lh-base mb-0 fs-11 text-500 fw-semibold font-base cursor-pointer"
      >
        {title}
      </Form.Check.Label>
    </Form.Check>
  );
};

FormCheck.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  inputClass: PropTypes.string,
  checkBoxClass: PropTypes.string,
  handleLegend: PropTypes.func
};

const TicketsCategory = ({ title, number, color }) => {
  return (
    <div>
      <h6 className={classNames(color, 'fs-9 d-flex align-items-center mb-1')}>
        {number}
      </h6>
      <h6 className="fs-11 text-600 mb-0 text-nowrap">{title}</h6>
    </div>
  );
};

TicketsCategory.propTypes = {
  title: PropTypes.string.isRequired,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string
};

const SummaryReportComparison = () => {
  let xAxisData = [];
  let cumulativeCashFlow = [];
  let earnings = [];
  const { treeMarkers } = useSelector(state => state.livingCarbonGrant);
  for (let i = 0; i < 10; i += 1) {
    xAxisData.push(2023 + (i + 1));
    cumulativeCashFlow.push((Math.random() * 2).toFixed(2));
    earnings.push(-Math.random().toFixed(2));
  }

  const projects = [
    {
      id: 1,
      title: 'Project A',
      description: 'Des 1',
      unit: 19,
      price: 69
    },
    {
      id: 2,
      title: 'Project B',
      description: 'Des 2',
      unit: 19,
      price: 69
    },
    {
      id: 3,
      title: 'Project C',
      description: 'Des 3',
      unit: 16,
      price: 34
    }
  ];

  const data = {
    treePlanting: [
      [
        'Tree Species',
        'Location',
        'Planting Date',
        'Expected Growth Rate (m/year)',
        'Expected Carbon Sequestration (kg/year)',
        'Soil Quality',
        'Water Availability',
        'Maintenance Plan',
        'Biodiversity Contribution',
        'Monitoring Frequency'
      ],
      [
        'Mangrove',
        'Coastal Vietnam',
        '2025-01-10',
        1.2,
        50,
        'High',
        'High',
        'Monthly irrigation and trimming',
        'High - supports marine ecosystems',
        'Quarterly'
      ],
      [
        'Acacia',
        'Central Highlands',
        '2024-11-20',
        1.5,
        35,
        'Moderate',
        'Medium',
        'Bi-annual pruning',
        'Moderate - soil enrichment',
        'Annually'
      ],
      [
        'Teak',
        'Northern Mountains',
        '2025-02-15',
        2.0,
        60,
        'Good',
        'Low',
        'Fertilizing every 6 months',
        'Moderate - shade and fauna shelter',
        'Semi-Annually'
      ]
    ],
    windTurbine: [
      ['Wind Turbine Carbon Offset Evaluation Report'],
      ['Site Information'],
      ['Location', 'Coastal Region'],
      ['Coordinates', '52.1, 4.3'],
      ['Wind Resource Class', 'Class III – Moderate'],
      [],
      ['Technical Specifications'],
      ['Turbine Model', 'Vestas V112-3.0 MW'],
      ['Rated Capacity', '3.0 MW'],
      ['Hub Height', '80 m'],
      ['Rotor Diameter', '112 m'],
      ['Number of Units', '5'],
      [],
      ['Energy Output'],
      ['Capacity Factor', '35%'],
      ['Annual Energy Production (AEP)', '45,000 MWh/year'],
      ['Grid Efficiency Losses', '5%'],
      [],
      ['Carbon Offset Potential'],
      ['Emission Factor for Grid Electricity', '0.45 kg CO₂/kWh'],
      ['Total Carbon Offset per Year', '20,250 tCO₂/year'],
      [],
      ['Project Lifespan'],
      ['Expected Operation Period', '20–25 years'],
      ['Total Lifetime Offset', '405,000–506,250 tCO₂'],
      [],
      ['Other Environmental Considerations'],
      ['Bird/Bat Impact', 'Low'],
      ['Noise & Visual Impact', 'Moderate'],
      ['Land Use Change', 'Minimal'],
      [],
      ['Certifications & Methodologies'],
      ['Standard', 'Gold Standard'],
      ['MRV Plan', 'Annual Monitoring']
    ],
    solarPanel: [
      ['Solar Panel Carbon Offset Evaluation Report'],
      ['Site Information'],
      ['Location', 'Utility-Scale Desert Site'],
      ['Coordinates', '35.6, -115.2'],
      ['Solar Irradiation Level', '5.5 kWh/m²/day'],
      [],
      ['Technical Specifications'],
      ['Panel Type', 'Mono-crystalline'],
      ['System Size', '1 MWp'],
      ['Inverter Efficiency', '96%'],
      ['Tilt & Orientation', 'South-facing, 25°'],
      [],
      ['Energy Output'],
      ['Annual Energy Production (AEP)', '1,600 MWh/year'],
      ['Degradation Factor', '0.5% per year'],
      ['Grid Losses', '3%'],
      [],
      ['Carbon Offset Potential'],
      ['Emission Factor for Grid Electricity', '0.45 kg CO₂/kWh'],
      ['Total Carbon Offset per Year', '720 tCO₂/year'],
      [],
      ['Project Lifespan'],
      ['Expected Operation Period', '25–30 years'],
      ['Total Lifetime Offset', '18,000–21,000 tCO₂'],
      [],
      ['Other Environmental Considerations'],
      ['Panel Recycling', 'Planned'],
      ['Land Use', 'Low'],
      ['Heat Island Mitigation', 'Positive'],
      [],
      ['Certifications & Methodologies'],
      ['Standard', 'Verra'],
      ['MRV Plan', 'Annual Reporting']
    ]
  };

  const topCourses = [
    {
      title: 'Advanced Design Tools for Modern Designs',
      trainer: 'Bill finger',
      published: '01/10/21',
      enrolled: '47,726',
      price: '$39.99'
    },
    {
      title: 'Photograpy Basics: Get Familiar Standing Behind Lens',
      trainer: 'Bruce Timm',
      published: '11/12/21',
      enrolled: '38,541',
      price: '$19.99'
    },
    {
      title: 'Abstract Painting: Zero to Mastery in Traditional Medium',
      trainer: 'J. H. Williams III',
      published: '03/09/21',
      enrolled: '35,666',
      price: '$45.49'
    }
  ];
  return (
    <>
      <Projects projects={projects} />

      <ExpenseBifurcation tableData={topCourses} className="my-3" />
      <Card className="h-100 my-3">
        <Card.Body className="py-0">
          <Row>
            <Col
              md="2"
              className="border-md-end border-md-200 pt-3 pt-md-0 d-md-flex align-items-center"
            >
              <Row className="flex-md-column px-3">
                <Col
                  xs="auto"
                  className="border-end border-md-end-0 border-md-bottom pb-md-3 mb-md-3 ps-0 ps-md-3 pe-x1 pe-md-3 me-1 me-md-0"
                >
                  <TicketsCategory
                    title="Return On Investment"
                    number="125%"
                    color="text-success"
                  />
                </Col>
                <Col
                  xs="auto"
                  className="border-end border-md-end-0 border-md-bottom pb-md-3 mb-md-3 pe-x1 pe-md-3 me-1 me-md-0"
                >
                  <TicketsCategory
                    title="Payback Period"
                    number="5 years"
                    color="text-primary"
                  />
                </Col>
                <Col xs="auto" className="pe-0">
                  <TicketsCategory
                    title="Initial Investment"
                    number={`$${treeMarkers.length * 2}`}
                    color="text-warning"
                  />
                </Col>
              </Row>
            </Col>
            <Col md="10">
              <CashFlowChart
                xAxisData={xAxisData}
                cumulativeCashFlow={cumulativeCashFlow}
                earnings={earnings}
              />
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="bg-body-tertiary py-2">
          <Row className="flex-between-center g-0">
            <Col xs="auto">
              <ExportToXLSX
                data={data}
                fileName="Carbon_Offset_Evaluation_Report"
              ></ExportToXLSX>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </>
  );
};

export default SummaryReportComparison;
